import { Layer } from "../map/layer"
import { isArrayEmpty } from "../utils/array"
import { isArray, isUndefined } from "../utils/utils"
import { IDatalayerViewer } from "./types"
import { ol_Feature, ol_style, ol_geom } from "@omnia/exports/openlayers";

type Source = number[][] | (() => PromiseLike<number[][]>)

export class DatalayerPoints extends Layer {

    private points: number[][]
    private provider: () => PromiseLike<number[][]>

    /**
     * Used to track loading of points:
     * - false: not loaded
     * - undefined: loading in progress
     * - true: loaded
     */
    private loaded: boolean | undefined = false

    private _cluster: boolean


    constructor(source: Source, private _viewer: IDatalayerViewer, visible: boolean, private readonly styleCache: { [key: string]: ol_style.Style[] }) {

        super()

        if (isArray(source)) {
            this.points = source
        }
        else {
            this.provider = source
        }

        this.selectable = false
        this.isVisible = visible

    }

    protected setVisible(value: boolean) {

        if (value && (!this.loaded)) {
            this.loadPoints()
        }

        this._layer.setVisible(value)

    }

    get cluster(): boolean {
        return this._cluster
    }

    set cluster(value: boolean) {
        if (value != this._cluster) {
            this._cluster = value
            this.clusterSource(value)
        }
    }

    private loadPoints() {


        const { points, provider, loaded } = this

        if ((!loaded) && provider) {

            if (loaded === undefined) {
                return
            }

            this.loaded = undefined

            provider().then(points => {
                this.loaded = true
                this.points = points
                this.loadPoints()
                this._layer.setVisible(this.visible)
            })

            return

        }

        if (!points) return

        let id: number = 0

        const source = points.map(p => {
            return {
                geometry: new ol_geom.Point([p[0], p[1]]),
                id: ++id,
                value: p[2],
            }
        })

        this.setSource(
            source,
            p => p.geometry,
            p => p.id
        )

        this._viewer.refresh()

    }

    getExtent(): [number, number, number, number] {

        const points = this.points

        if (!isArrayEmpty(points)) {

            var p = points[0]
            var xmin = p[0], xmax = p[0]
            var ymin = p[1], ymax = p[1]

            for (var i = 1; i < points.length; i++) {
                p = points[i]
                xmin = Math.min(xmin, p[0])
                xmax = Math.max(xmax, p[0])
                ymin = Math.min(ymin, p[1])
                ymax = Math.max(ymax, p[1])
            }

            return [xmin, ymin, xmax, ymax]

        }

    }

    getLayerStyleFunction(selected: boolean) {

        return (feature: ol_Feature, resolution: number) => {

            let radius: number = (resolution > 10 ? 2 : 5)
            let color: number[]

            if (this._cluster) {

                let features = feature.get("features")

                const maxFeatures = 200

                radius += Math.round((Math.min(maxFeatures, features.length) / maxFeatures) * 5)

                if (features.length == 1) {
                    feature = features[0]
                }
                else {
                    const values = features.map(f => f.get("data").value)
                    color = this._viewer.getClusterPointFillColor(values)
                }

            }

            if (isUndefined(color)) {
                color = this._viewer.getPointFillColor(feature)
            }

            const key = color.concat([radius]).join("/")

            let style = this.styleCache[key]

            if (!style) {

                style = [
                    new ol_style.Style({
                        image: new ol_style.Circle({
                            fill: new ol_style.Fill({ color: color }),
                            stroke: new ol_style.Stroke({ color: '#333', width: 0.5 }),
                            radius
                        }),
                    })
                ]

                this.styleCache[key] = style

            }

            return style

        }

    }

}
